/**
 * Module dependencies.
 */

import { AppointmentFormData } from 'src/requests/appointment';
import { AxiosError } from 'axios';
import { Button } from 'src/components/core/buttons/button';
import { DateTimeFields } from 'src/components/forms/shared/date-time-field';
import { Form } from 'src/components/forms/shared/styles';
import { InputField } from 'src/components/core/forms/input/field';
import { NewsletterAndTermsFields } from 'src/components/forms/shared/newsletter-terms-fields';
import { PageModalLayout } from 'src/components/layout/page-modal';
import {
  PromoCodeFieldWrapper,
  promoCodeFieldName,
  useValidatePromoCode
} from './shared/promo-code-field';

import { ReactNode, useCallback, useState } from 'react';
import { RecaptchaField } from 'src/components/core/forms/recaptcha';
import { UseFormReturn, useForm } from 'react-hook-form';
import { UserInfoFields } from 'src/components/forms/shared/user-info-fields';
import { useTranslation } from 'next-i18next';
import iconButton from 'src/assets/svg/button.svg';
import styled from 'styled-components';

/**
 * `Methods` type.
 */

type Methods = UseFormReturn<AppointmentFormData, any>;

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode | ((methods: Methods) => ReactNode);
  modalCloseButtonLink: string;
  onSubmit: (data: any, reset: () => void) => void;
};

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  width: max-content;
`;

/**
 * Export `BookAppointmentForm` component.
 */

export function BookAppointmentForm(props: Props) {
  const { children, modalCloseButtonLink, onSubmit } = props;
  const { t } = useTranslation(['common', 'appointment']);
  const [promoCodeMessage, setPromoCodeMessage] = useState<
    string | undefined
  >();

  const formMethods = useForm<AppointmentFormData>({
    defaultValues: {
      subscribeToNewsletter: true
    }
  });

  const {
    clearErrors,
    control,
    formState,
    getValues,
    handleSubmit,
    reset,
    setError
  } = formMethods;

  const handleOnSubmit = useCallback(
    async (data: AppointmentFormData) => {
      const responseData = await onSubmit(data, reset);
      const response = (
        responseData as unknown as AxiosError<{ message: string }>
      )?.response;

      if (response?.data?.message === 'Invalid promotional code') {
        setError(promoCodeFieldName, { message: t('common:promoCode.error') });
      }
    },
    [onSubmit, reset, setError, t]
  );

  const [previousPromoCode, handleValidatePromoCode] = useValidatePromoCode({
    getValues,
    onError: () => {
      setError(promoCodeFieldName, { message: t('common:promoCode.error') });
    },
    onSuccess: () => {
      clearErrors(promoCodeFieldName);
      setPromoCodeMessage(t('common:promoCode.success'));
    }
  });

  return (
    <PageModalLayout
      aside={typeof children === 'function' ? children(formMethods) : children}
      closeButtonLink={modalCloseButtonLink}
      subtitle={t('appointment:subtitle')}
      title={t('appointment:title')}
    >
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <UserInfoFields control={control} />

        <InputField
          as={'textarea'}
          control={control}
          label={t('common:form.labels.preConditions')}
          name={'preconditions'}
          placeholder={t('common:form.placeholders.preConditions')}
          rows={4}
        />

        <InputField
          as={'textarea'}
          control={control}
          label={t('common:form.labels.observations')}
          name={'observations'}
          placeholder={t('common:form.placeholders.observations')}
          rows={4}
        />

        <DateTimeFields control={control} />

        <PromoCodeFieldWrapper
          control={control as any}
          onClick={handleValidatePromoCode}
          previousPromoCode={previousPromoCode}
        >
          <InputField
            control={control}
            info={promoCodeMessage}
            label={t('form.labels.promoCode')}
            name={promoCodeFieldName}
            placeholder={t('form.placeholders.promoCode')}
          />
        </PromoCodeFieldWrapper>

        <NewsletterAndTermsFields control={control} />

        <RecaptchaField
          action={'appointmentBooking'}
          control={control as any}
        />

        <StyledButton
          icon={iconButton}
          isLoading={formState.isSubmitting}
          type={'submit'}
        >
          {t('common:actions.sendMessage')}
        </StyledButton>
      </Form>
    </PageModalLayout>
  );
}
